/**
 * Canonical Flight Model.
 *
 * A flight is a columnar time-series of typed, self-describing channels.
 * Importers (DJI SRT, future vendors) normalize their raw fields into this
 * shape; every lens (player, map, charts, 3D, export) is only ever a reader of
 * it. Nothing downstream is allowed to know vendor-specific field names.
 */

/** What a channel measures — drives default rendering and math. */
export type ChannelKind = 'scalar' | 'angle' | 'position' | 'categorical';

/** Whether a channel is measured or computed. */
export type ChannelOrigin = 'raw' | 'derived';

/**
 * §1.5 Which raw source a channel was resolved from. Orthogonal to `origin`
 * (a `derived` channel has no source provenance). `'mixed'` = resolved from more
 * than one source across the time axis (e.g. `.txt` for taxi, `djmd` for the
 * recording window). Drives the "which source" / verify UI (§2.4) only.
 */
export type ChannelProvenance = 'srt' | 'log' | 'video' | 'mixed';

export interface ChannelDef {
  /** Canonical key, e.g. "ground_speed". Stable across vendors. */
  key: string;
  label: string;
  /** Canonical SI-ish unit the values are stored in (m, m/s, deg, °C, ...). */
  unit: string;
  kind: ChannelKind;
  origin: ChannelOrigin;
  /** §1.5 raw-source provenance (undefined for derived channels). */
  provenance?: ChannelProvenance;
  /** Min/max over the flight, computed on import. Null for categorical. */
  range: { min: number; max: number } | null;
}

/** §1.6 Per-flight device identity (from the djmd identity header). */
export interface DeviceInfo {
  serial?: string;
  model?: string;
  firmware?: string;
  fcVersion?: string;
}

/** §1.6 Per-flight media metadata (SD-card path, recording timezone). */
export interface MediaInfo {
  sdCardPath?: string;
  /** Minutes east of UTC (from filename-local vs mvhd-UTC), when known. */
  tzOffsetMin?: number;
}

export interface FlightSource {
  vendor: string;
  model: string;
  importer: string;
  importerVersion: string;
}

export interface GeoPoint {
  lat: number;
  lon: number;
  /** Absolute altitude (m, MSL) if known. */
  absAlt?: number;
}

/**
 * Columnar sample store. `time` is milliseconds from flight start (t0).
 * Numeric channels are parallel arrays keyed by ChannelDef.key.
 * Categorical channels live in `categories` as string arrays.
 */
export interface SampleStore {
  /** Sample count. */
  length: number;
  /** ms from flight start, monotonically non-decreasing. */
  time: Float64Array;
  /** key -> values (NaN where missing for that sample). */
  numeric: Record<string, Float64Array>;
  /** key -> values for categorical channels. */
  categories: Record<string, string[]>;
}

export interface Flight {
  id: string;
  source: FlightSource;
  /** Takeoff reference for the local ENU frame and distance-from-home. */
  origin: GeoPoint;
  /** Wall-clock of the first sample (UTC ISO). */
  startedAt: string;
  durationMs: number;
  channels: ChannelDef[];
  samples: SampleStore;
  /** §1.6 device identity, when a djmd track supplied it. */
  device?: DeviceInfo;
  /** §1.6 media metadata, when known. */
  media?: MediaInfo;
}

/** A flat snapshot of all channels at one sample index — what widgets read. */
export interface SampleView {
  index: number;
  timeMs: number;
  /** key -> numeric value (may be NaN). */
  num: Record<string, number>;
  /** key -> categorical value. */
  cat: Record<string, string>;
}

export function channelByKey(flight: Flight, key: string): ChannelDef | undefined {
  return flight.channels.find((c) => c.key === key);
}

/** Build a flat view at a sample index. */
export function viewAt(flight: Flight, index: number): SampleView {
  const s = flight.samples;
  const i = Math.max(0, Math.min(s.length - 1, index));
  const num: Record<string, number> = {};
  for (const key in s.numeric) num[key] = s.numeric[key][i];
  const cat: Record<string, string> = {};
  for (const key in s.categories) cat[key] = s.categories[key][i];
  return { index: i, timeMs: s.time[i], num, cat };
}

/**
 * Binary search the sample index whose time is the greatest <= timeMs.
 * Telemetry is sampled densely (per video frame), so this maps a video
 * playback time to a sample in O(log n).
 */
export function indexAtTime(flight: Flight, timeMs: number): number {
  const t = flight.samples.time;
  let lo = 0;
  let hi = t.length - 1;
  if (timeMs <= t[0]) return 0;
  if (timeMs >= t[hi]) return hi;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (t[mid] <= timeMs) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}
